import React, { useEffect, useState, useRef } from "react";
import { Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl, faSearch, faCalendarAlt, faEye } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import swal from "sweetalert";
import { APISD } from "../../../../../config/apisd"; // sesuaikan config API siakad
import { APILA } from "../../../../../config/apila";
import { APIMS } from "../../../../../config/apims";
import { APIUS } from "../../../../../config/apius";
import LoaderHome from "../../../../Loader/LoaderHome";

// ─── Helper: warna status absen ──────────────────────────────────────────────
const STATUS_COLOR = {
  hadir:          { bg: '#16a34a', text: '#fff' },
  izin:           { bg: '#2563eb', text: '#fff' },
  sakit:          { bg: '#6b7280', text: '#fff' },
  alpa:           { bg: '#e1ef40', text: '#000000' },
  libur:          { bg: '#f9a8d4', text: '#9f1239' }, // pink — weekend
  libur_nasional: { bg: '#f97316', text: '#fff' },    // oranye — libur kalender akademik
  default:        { bg: '#e5e7eb', text: '#6b7280' },
};

const statusColor = (s) => STATUS_COLOR[s] || STATUS_COLOR.default;

// ─── Helper: bangun grid kalender bulanan ────────────────────────────────────
const buildCalendarGrid = (year, month, calendarArr) => {
  const statusMap = {};
  const titleMap  = {};
  (calendarArr || []).forEach(c => {
    statusMap[c.date] = c.status;
    if (c.title) titleMap[c.date] = c.title;
  });

  const firstDay  = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const isoFirst  = firstDay === 0 ? 6 : firstDay - 1;     // iso: 0=Mon
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < isoFirst; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const mm  = String(month).padStart(2, '0');
    const dd  = String(d).padStart(2, '0');
    const key = `${year}-${mm}-${dd}`;
    const dow = new Date(year, month - 1, d).getDay(); // 0=Sun
    const isWeekend = dow === 0 || dow === 6;
    cells.push({
      day: d, date: key,
      status: isWeekend ? 'libur' : (statusMap[key] || null),
      title: titleMap[key] || null,
    });
  }
  return cells;
};

// ─── Legend item ─────────────────────────────────────────────────────────────
const LegendDot = ({ label, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
    <span style={{ fontSize: 11, color: '#374151' }}>{label}</span>
  </div>
);

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, color, icon, sub }) => (
  <div style={{
    position: 'relative', background: '#fff', borderRadius: 10,
    padding: 18, boxShadow: '0 6px 18px rgba(15,23,42,0.06)', overflow: 'hidden'
  }}>
    <div style={{
      position: 'absolute', left: 0, top: 0, bottom: 0,
      width: 6, background: color, borderRadius: '6px 0 0 6px'
    }} />
    <div style={{
      position: 'absolute', right: 12, top: 6,
      fontSize: 52, opacity: 0.07, color
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{sub}</div>}
    </div>
  </div>
);

// ─── Attendance Calendar Modal ────────────────────────────────────────────────
const AttendanceModal = ({ isOpen, toggle, student, calendarData, year, month }) => {
  const [viewYear, setViewYear]   = useState(year);
  const [viewMonth, setViewMonth] = useState(month);

  useEffect(() => {
    if (isOpen) { setViewYear(year); setViewMonth(month); }
  }, [isOpen, year, month]);

  const cells = buildCalendarGrid(viewYear, viewMonth, calendarData);
  const DAY_LABELS = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

  const monthName = new Date(viewYear, viewMonth - 1, 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const photoUrl = student?.photo
    ? student.photo
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(student?.name_student || 'S')}&background=0d6efd&color=fff&size=80`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} style={{ borderBottom: '1px solid #e5e7eb', padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={photoUrl}
            alt={student?.name_student}
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb' }}
            onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student?.name_student || 'S')}&background=0d6efd&color=fff&size=80`; }}
          />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{student?.name_student || '-'}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Detail Kehadiran</div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '20px 24px' }}>
        {/* Nav bulan */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button onClick={prevMonth} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#111827', textTransform: 'capitalize' }}>{monthName}</span>
          <button onClick={nextMonth} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>›</button>
        </div>

        {/* Grid hari */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 16 }}>
          {DAY_LABELS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#9ca3af', paddingBottom: 6 }}>{d}</div>
          ))}
          {cells.map((cell, i) => {
            if (!cell) return <div key={`e-${i}`} />;
            const sc = cell.status ? statusColor(cell.status) : STATUS_COLOR.default;
            return (
              <div key={cell.date} title={cell.title || cell.status || ''} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                aspectRatio: '1', borderRadius: 8,
                background: sc.bg, color: sc.text,
                fontSize: 13, fontWeight: 700, cursor: 'default',
                opacity: cell.status === null ? 0.35 : 1,
              }}>
                {cell.day}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        {(() => {
          const holidayTitles = [...new Set(
            (calendarData || [])
              .filter(c => c.status === 'libur_nasional' && c.title)
              .map(c => c.title)
          )];
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
              <LegendDot label="Hadir"      color={STATUS_COLOR.hadir.bg} />
              <LegendDot label="Terlambat"  color="#00e3ef" />
              <LegendDot label="Sakit"      color={STATUS_COLOR.sakit.bg} />
              <LegendDot label="Izin"       color={STATUS_COLOR.izin.bg} />
              <LegendDot label="Alpa"       color={STATUS_COLOR.alpa.bg} />
              <LegendDot label="Libur"      color={STATUS_COLOR.libur.bg} />
              {holidayTitles.map(title => (
                <LegendDot key={title} label={title} color={STATUS_COLOR.libur_nasional.bg} />
              ))}
            </div>
          );
        })()}
      </ModalBody>

      <ModalFooter style={{ borderTop: '1px solid #e5e7eb', padding: '10px 20px' }}>
        <Button color="secondary" size="sm" onClick={toggle}>Tutup</Button>
      </ModalFooter>
    </Modal>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomeTeacherAbsensi() {
  document.title = "Dashboard Absensi";

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 780px)' });
  const location = useLocation();
  const navigate = useNavigate();

  const token          = localStorage.getItem("token");
  const storageBranch  = localStorage.getItem('ysb_branch_id') || "";
  const storageSchool  = localStorage.getItem('ysb_school_id') || "";
  const storageTeacher = localStorage.getItem('teacherID')     || "";
  const storageLevel   = localStorage.getItem('level')         || "";


  const fetchParams = {
    headers: { "Authorization": `${token}`, "Content-Type": "application/json" }
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading]           = useState(false);
  const [searchTerm, setSearchTerm]     = useState("");
  const [getDataSemester, setGetDataSemester] = useState(null);
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [filterSemesterId, setFilterSemesterId] = useState("");
  const [isInitialized, setIsInitialized]     = useState(false);

  const [summary, setSummary] = useState({
    total_siswa: 0,
    total_hari_efektif: 0,
    total_hadir: 0,
    total_izin: 0,
    total_sakit: 0,
    total_alpa: 0,
    total_terlambat: 0,
    persentase_kehadiran: 0,
  });

  const [items, setItems] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen]         = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Guard refs
  const autoFetchedRef      = useRef(false);
  const lastFetchParamsRef  = useRef(null);

  // permission
  const [permission, setPermission] = useState({ read: 0 });

  useEffect(() => {
    const storageItems = JSON.parse(localStorage.getItem('role_permission')) || [];
    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({ read: 1 });
    } else {
      const found = storageItems.find(item => item.menu?.url === location.pathname);
      if (!found) { navigate('/dashboard'); }
      else setPermission({ read: found.read });
    }
  }, [location.pathname]);

  // ── Fetch semester aktif ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchSemester = async () => {
      try {
        const page = 1;
        const limit = 1000;
        const descending = 0;
        const keyword = "";

        // const resp = await APILA.get(
        //   `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
        //   fetchParams
        // );

        const resp = await APIMS.get(
          `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
          fetchParams
        );

        if (resp.status === 200) {
          const semesterList = resp.data?.data || [];
          const active = semesterList.find(s => s.status === 1) || semesterList[0];
          setSemesterOptions(semesterList);
          setGetDataSemester(active || null);
          setFilterSemesterId(active?.id ? String(active.id) : "");
        }
      } catch (e) {
        swal({
          title: 'Gagal',
          text: e.response?.data?.message || 'Gagal mengambil data semester',
          icon: 'error',
          timer: 3000,
          buttons: false
        });
      } finally {
        setIsInitialized(true);
      }
    };

    fetchSemester();
  }, []);

  // ── Tanggal awal/akhir bulan ini (default filter) ─────────────────────────
  const today     = new Date();
  const startOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const endOfMonth   = (() => {
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
  })();

  const [filterStartDate, setFilterStartDate] = useState(startOfMonth);
  const [filterEndDate, setFilterEndDate]     = useState(endOfMonth);

  // ── Fetch data dashboard absensi ──────────────────────────────────────────
  const fetchDashboard = async (semesterId, startDate, endDate) => {
    if (!semesterId || !storageBranch || !storageSchool || !storageTeacher) return;

    try {
      setLoading(true);
      const resp = await APISD.get('/api/absen-dashboards/teacher', {
        params: {
          ysb_semester_id: semesterId,
          ysb_branch_id:   storageBranch,
          ysb_school_id:   storageSchool,
          ysb_teacher_id:  storageTeacher,
          start_date:      startDate,
          end_date:        endDate,
          limit:           1000,
          page:            1,
        },
        ...fetchParams
      });

      if (resp.status === 200) {
        setSummary(resp.data?.summary || {});
        setItems(resp.data?.items || []);
        autoFetchedRef.current = true;
        lastFetchParamsRef.current = { semesterId, startDate, endDate };
      }
    } catch (err) {
      swal({ title: 'Gagal', text: err.response?.data?.message || 'Terjadi kesalahan', icon: 'error', timer: 3000, buttons: false });
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch saat semua params siap
  useEffect(() => {
    if (!isInitialized) return;
    const selectedSemesterId = filterSemesterId || getDataSemester?.id;
    if (!selectedSemesterId) return;
    fetchDashboard(selectedSemesterId, filterStartDate, filterEndDate);
  }, [isInitialized, getDataSemester?.id, filterSemesterId]);

  // Refetch saat filter tanggal berubah (debounced)
  const filterTimer = useRef(null);
  const handleFilterChange = () => {
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      const selectedSemesterId = filterSemesterId || getDataSemester?.id;
      if (selectedSemesterId) fetchDashboard(selectedSemesterId, filterStartDate, filterEndDate);
    }, 600);
  };
  useEffect(() => { handleFilterChange(); return () => clearTimeout(filterTimer.current); }, [filterStartDate, filterEndDate, filterSemesterId]);

  // ── Filtered & sorted items ───────────────────────────────────────────────
  const filteredItems = (items || []).filter(s => {
    if (!searchTerm) return true;
    return (s.name_student || "").toLowerCase().includes(searchTerm.toLowerCase());
  });

  // ── Open modal ────────────────────────────────────────────────────────────
  const openModal = (student) => {
    setSelectedStudent(student);
    setModalOpen(true);
  };

  // Tahun & bulan untuk kalender modal (dari start_date filter)
  const calYear  = parseInt(filterStartDate.split('-')[0]);
  const calMonth = parseInt(filterStartDate.split('-')[1]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="body">
      <div className="body-header d-flex">
        <div className="title-page">
          {isTabletOrMobile
            ? <h6><FontAwesomeIcon icon={faListUl} /> Dashboard Absensi</h6>
            : <h5><FontAwesomeIcon icon={faListUl} /> Dashboard Absensi</h5>
          }
        </div>
      </div>

      <div className="body-content">
        {loading && <LoaderHome />}

        {/* ── Filter tanggal ── */}
        <Row className="mb-3 align-items-end">
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>
              <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />Dari Tanggal
            </label>
            <input
              type="date" className="form-control form-control-sm"
              value={filterStartDate}
              onChange={e => setFilterStartDate(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>
              <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 6 }} />Sampai Tanggal
            </label>
            <input
              type="date" className="form-control form-control-sm"
              value={filterEndDate}
              onChange={e => setFilterEndDate(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={12} md={4}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>
              <FontAwesomeIcon icon={faListUl} style={{ marginRight: 6 }} />Semester
            </label>
            <select
              className="form-control form-control-sm"
              value={filterSemesterId}
              onChange={e => setFilterSemesterId(e.target.value)}
              style={{ borderRadius: 8 }}
            >
              {semesterOptions.length === 0 && <option value="">Semester aktif</option>}
              {semesterOptions.map((semester) => (
                <option key={semester.id} value={semester.id}>
                  Tahun Ajaran {semester.name_year} - Semester {semester.semester}
                </option>
              ))}
            </select>
          </Col>
        </Row>

        {/* ── Summary Cards — Baris 1: 4 card ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isTabletOrMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 12
        }}>
          <SummaryCard label="Total Siswa"       value={summary.total_siswa}  color="#3b82f6" icon="👥" sub="Siswa terdaftar" />
          <SummaryCard label="Total Siswa Hadir" value={summary.total_hadir}  color="#16a34a" icon="✅" sub="Siswa hadir ≥1 hari" />
          <SummaryCard label="Total Siswa Izin"  value={summary.total_izin}   color="#2563eb" icon="📋" sub="Siswa izin ≥1 hari" />
          <SummaryCard label="Total Siswa Sakit" value={summary.total_sakit}  color="#6b7280" icon="🤒" sub="Siswa sakit ≥1 hari" />
        </div>

        {/* ── Summary Cards — Baris 2: 3 card ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isTabletOrMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 16
        }}>
          <SummaryCard label="Total Siswa Alpa"      value={summary.total_alpa}                    color="#dc2626" icon="⚠️" sub="Siswa alpa ≥1 hari" />
          <SummaryCard label="Total Siswa Terlambat" value={summary.total_terlambat}               color="#f59e0b" icon="⏰" sub="Siswa terlambat ≥1 hari" />
          <SummaryCard label="Persentase Kehadiran"  value={`${summary.persentase_kehadiran}%`}    color="#7c3aed" icon="📊" sub="Kehadiran keseluruhan" />
        </div>

        {/* ── Tabel siswa ── */}
        
      </div>

      {/* ── Attendance Calendar Modal ── */}
      {selectedStudent && (
        <AttendanceModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          student={selectedStudent}
          calendarData={selectedStudent.calendar || []}
          year={calYear}
          month={calMonth}
        />
      )}
    </div>
  );
}